import { Helmet, HelmetProvider } from 'react-helmet-async';
import { Card, CardBody, CardHeader, Tooltip, Spinner } from "@heroui/react";
import {
  ChartLine,
  Buildings,
  Warning,
  Files,
  CurrencyCircleDollar,
  ListBullets,
  ChartLineUp,
  ProhibitInset,
  House,
  Storefront,
  TrendUp,
  Calendar,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { InfoCard } from "components/infoCard/infoCard";
import { DataCard } from "components/dataCard/dataCard";
import { applyMask } from "shared/utils/masks";
import {
  useRentalMetrics,
  useRentalStats,
  useUnitsInRenovation,
  useActiveContractsCount,
  useUpsellingMetrics
} from "../../hooks";
import { UnitDTO } from "shared/types/unit";
import { CompanyPortfolioBlockNew } from "screens/home/componentsNew/blocks/CompanyPortfolioBlockNew";

interface ContractDetail {
  id: number;
  value: number;
  unitNumber?: string;
  tenantNames?: string;
}

const RentalStatistics = () => {
  const { metrics, isLoading: isLoadingMetrics } = useRentalMetrics();
  const { stats: rentalStats } = useRentalStats();
  const { unitsInRenovation } = useUnitsInRenovation();
  const { count: activeContractsCount } = useActiveContractsCount();
  const { metrics: upsellingMetrics } = useUpsellingMetrics();

  const unitsInRenovationErv = unitsInRenovation.reduce(
    (sum: number, unit: UnitDTO) => sum + (unit.estimatedRentalValue || 0),
    0
  );

  const formatTooltipContent = (units: Array<{ unitId: string; surface: number; rent: number }>) => {
    if (!units || units.length === 0) {
      return (
        <div className="p-4 text-neutral-600 text-center">
          Geen units in deze categorie
        </div>
      );
    }

    const totalRent = units.reduce((sum, unit) => sum + unit.rent, 0);
    const totalSurface = units.reduce((sum, unit) => sum + unit.surface, 0);

    return (
      <div className="p-4">
        <table className="min-w-[400px]">
          <thead>
            <tr>
              <th className="py-2 px-4 text-left border-b border-neutral-200 font-medium">Unit</th>
              <th className="py-2 px-4 text-right border-b border-neutral-200 font-medium">Opp. (m²)</th>
              <th className="py-2 px-4 text-right border-b border-neutral-200 font-medium">Huurprijs</th>
              <th className="py-2 px-4 text-right border-b border-neutral-200 font-medium">€/m²</th>
            </tr>
          </thead>
          <tbody>
            {units.map(unit => (
              <tr key={unit.unitId} className="hover:bg-neutral-50">
                <td className="py-2 px-4 border-b border-neutral-100">{unit.unitId}</td>
                <td className="py-2 px-4 text-right border-b border-neutral-100">
                  {unit.surface.toFixed(1)}
                </td>
                <td className="py-2 px-4 text-right border-b border-neutral-100">
                  {applyMask(unit.rent, "valuta")}
                </td>
                <td className="py-2 px-4 text-right border-b border-neutral-100">
                  {(unit.rent / unit.surface).toFixed(2)}
                </td>
              </tr>
            ))}
            <tr className="font-medium bg-neutral-50">
              <td className="py-3 px-4 border-t border-neutral-200">Totaal</td>
              <td className="py-3 px-4 text-right border-t border-neutral-200">
                {totalSurface.toFixed(1)}
              </td>
              <td className="py-3 px-4 text-right border-t border-neutral-200">
                {applyMask(totalRent, "valuta")}
              </td>
              <td className="py-3 px-4 text-right border-t border-neutral-200">
                {(totalRent / totalSurface).toFixed(2)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  if (isLoadingMetrics) {
    return (
      <div className="flex h-[400px] items-center justify-center bg-neutral-50">
        <Spinner size="lg" color="primary" label="Statistieken laden..." classNames={{
          wrapper: "flex flex-col items-center gap-4",
          circle1: "border-b-moss-primary",
          label: "text-moss-primary font-medium"
        }} />
      </div>
    );
  }

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Verhuur Statistieken | Yally</title>
        </Helmet>
      </HelmetProvider>

      <div className="grid gap-8 sm:col-span-full sm:grid-cols-2">
        <div className="flex flex-col gap-2.5">
          <header className="flex items-center gap-1.5">
            <ChartLine weight="duotone" size={22} />
            <p className="text-lg">Verhuur status</p>
          </header>
          <div className="grid grid-cols-2 grid-rows-2 items-stretch gap-3 sm:gap-4">
            <Link to="/rental/vacancies">
              <InfoCard
                header={metrics.vacantUnits.toString()}
                title="Leegstaande units"
                label={`ERV: ${applyMask(metrics.vacantUnitsErv, "valuta")} / maand`}
                icon={<Buildings weight="duotone" />}
              />
            </Link>

            <Link to="/rental/vacancies">
              <InfoCard
                header={metrics.upcomingVacantUnits.toString()}
                title="Aankomende leegstand"
                label={`ERV: ${applyMask(metrics.upcomingVacantUnitsErv, "valuta")} / maand`}
                icon={<Buildings weight="duotone" />}
              />
            </Link>

            <InfoCard
              title="Units in renovatie"
              header={unitsInRenovation?.length.toString() || "0"}
              label={`ERV: ${applyMask(unitsInRenovationErv, "valuta")} / maand`}
              icon={<House size={20} />}
            />

            <Link to="/rental/watchlist">
              <InfoCard
                header={metrics.watchlistContracts.toString()}
                title="Contracten op watchlist"
                icon={<Warning weight="duotone" />}
              />
            </Link>

            <Link to="/rental/missing-documents">
              <InfoCard
                header={metrics.missingDocuments.toString()}
                title="Ontbrekende documenten"
                icon={<Files weight="duotone" />}
              />
            </Link>

            <Link to="/rental/incasso">
              <InfoCard
                header={metrics.outstandingInvoices.toString()}
                total={applyMask(metrics.outstandingAmount, "valuta")}
                title="Openstaande facturen"
                icon={<CurrencyCircleDollar weight="duotone" />}
              />
            </Link>

            <Link to="/rental/contracts-this-year">
              <InfoCard
                header={metrics.contractsCreatedThisYear?.toString() || "0"}
                total={
                  <Tooltip
                    content={
                      <div className="p-2">
                        <div className="font-bold mb-2">Contract Details:</div>
                        {metrics.contractsCreatedThisYearDetails?.map((contract: ContractDetail) => (
                          <div key={contract.id} className="mb-1">
                            {contract.unitNumber || 'Unit'}: {contract.tenantNames || 'Tenant'} -
                            {applyMask(contract.value, "valuta")}
                          </div>
                        ))}
                      </div>
                    }
                  >
                    <span>
                      = {applyMask(metrics.contractsCreatedThisYearValue, "valuta")} sinds start jaar
                    </span>
                  </Tooltip>
                }
                title="Afgesloten huurcontracten dit jaar"
                icon={<ProhibitInset weight="duotone" />}
              />
            </Link>
          </div>
        </div>

        <div className="flex flex-col gap-2.5">
          <header className="flex items-center gap-1.5">
            <ChartLineUp weight="duotone" size={22} />
            <h2 className="text-lg font-medium">Verhuur metrics</h2>
          </header>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <InfoCard
              header={activeContractsCount.toString()}
              title="Actieve contracten"
              icon={<ListBullets weight="duotone" />}
            />

            <Tooltip
              content={
                <div className="p-4">
                  <div className="mb-3">
                    <div className="font-medium mb-1">Units bezetting</div>
                    <div>{metrics.occupancyRateByUnits}% ({rentalStats.occupancyDetails?.activeUnits || 0}/{rentalStats.occupancyDetails?.totalUnits || 0} units)</div>
                    {rentalStats.occupancyDetails?.unitsWithoutContract &&
                      <div className="mt-2 text-sm text-neutral-600">
                        Units zonder actief contract:
                        <ul className="mt-1 list-disc list-inside">
                          {rentalStats.occupancyDetails.unitsWithoutContract.map((unit: UnitDTO) => (
                            <li key={unit.id}>{unit.id}</li>
                          ))}
                        </ul>
                      </div>
                    }
                  </div>
                  <div>
                    <div className="font-medium mb-1">ERV bezetting</div>
                    <div>
                      {metrics.occupancyRateByERV}% (
                      {applyMask(rentalStats.occupancyDetails?.totalActiveContractualRentRent || 0, "valuta")}/
                      {applyMask(rentalStats.occupancyDetails?.totalERV || 0, "valuta")})
                    </div>
                    {rentalStats.occupancyDetails?.unitsWithoutERV &&
                      <div className="mt-2 text-sm text-neutral-600">
                        Units zonder ERV:
                        <ul className="mt-1 list-disc list-inside">
                          {rentalStats.occupancyDetails.unitsWithoutERV.map((unit: UnitDTO) => (
                            <li key={unit.id}>{unit.id}</li>
                          ))}
                        </ul>
                      </div>
                    }
                  </div>
                </div>
              }
              placement="bottom"
            >
              <div>
                <InfoCard
                  header={`${metrics.occupancyRate}%`}
                  title="Bezetting"
                  total={`Units: ${metrics.occupancyRateByUnits}% | ERV: ${metrics.occupancyRateByERV}%`}
                  icon={<ChartLine weight="duotone" />}
                />
              </div>
            </Tooltip>

            <DataCard
              title={applyMask(metrics.totalRentalIncome, "valuta")}
              icon={<ChartLineUp weight="duotone" />}
              subtitle="Maandelijkse contractuele huurinkomsten (excl. provisies en OV)"
            />

            <DataCard
              title={applyMask(upsellingMetrics?.totalThisYear || 0, "valuta")}
              icon={<TrendUp size={20} />}
              subtitle="Upselling dit jaar"
              label={`Totaal over alle jaren heen: ${applyMask(upsellingMetrics?.totalAllYears || 0, "valuta")}`}
            />

            <Link to="/rental/prices">
              <DataCard
                title={metrics.upcomingIndexations.toString()}
                icon={<Calendar weight="duotone" />}
                subtitle="Naderende indexaties"
                label="komende 3 maanden"
              />
            </Link>

            <DataCard
              title={applyMask(rentalStats.residential.averageActualRent, "valuta")}
              icon={<House weight="duotone" />}
              subtitle="Gemiddelde residentiële huur"
              label="per maand"
            />

            <DataCard
              title={applyMask(rentalStats.retail.averageActualRent, "valuta")}
              icon={<Storefront weight="duotone" />}
              subtitle="Gemiddelde retail huur"
              label="per maand"
            />

            <Card className="col-span-2">
              <CardHeader>
                <h3 className="text-sm font-medium">Residentiële huur per oppervlakte</h3>
              </CardHeader>
              <CardBody>
                <div className="grid grid-cols-4 gap-2">
                  {Object.entries(rentalStats.residential.ranges).map(([range, data]) => {
                    const rangeLabel = {
                      range0_50: "0 - 50 m²",
                      range51_75: "51 - 75 m²",
                      range76_100: "76 - 100 m²",
                      range100plus: "> 100 m²"
                    }[range];

                    return (
                      <Tooltip
                        key={range}
                        content={data.units && data.units.length > 0
                          ? formatTooltipContent(data.units)
                          : <div className="p-2">Geen units in deze categorie</div>
                        }
                        placement="top"
                      >
                        <Card className="cursor-help hover:shadow-md transition-shadow">
                          <CardBody className="p-4">
                            <div className="flex flex-col">
                              <span className="text-sm text-neutral-600">{rangeLabel}</span>
                              <span className="text-xl font-semibold mt-2">
                                {applyMask(data.averageActualRent, "valuta")}
                              </span>
                              <span className="text-sm text-neutral-500 mt-2">
                                Gem. huurprijs ({data.count} units)
                              </span>
                              <span className="text-sm text-neutral-500 mt-1">
                                {applyMask(data.averageActualRentPerSqm, "valuta")}/m²
                              </span>
                            </div>
                          </CardBody>
                        </Card>
                      </Tooltip>
                    );
                  })}
                </div>
              </CardBody>
            </Card>
          </div>
        </div>
      </div>

      <div className="mt-8 sm:col-span-full">
        <header className="mb-4 flex items-center gap-1.5">
          <Buildings weight="duotone" size={22} />
          <p className="text-lg">Portfolio per vennootschap</p>
        </header>
        <Card>
          <CardBody>
            <CompanyPortfolioBlockNew embedded />
          </CardBody>
        </Card>
      </div>
    </>
  );
};

export default RentalStatistics;
